/**
 * macOS Keychain export for the containerised Synkro install.
 *
 * Claude Code on macOS stores its auth blob in the user's login keychain under
 * the service name `Claude Code-credentials`. A Linux container can't reach
 * the keychain, so on `synkro install` we shell out to the `security` CLI,
 * extract the blob, and write it to a host-side file that the container
 * bind-mounts read-write.
 *
 * Refresh policy: claude rotates its token periodically. We install a launchd
 * agent that re-exports every 45 min so the container picks up new tokens
 * without manual intervention. Stale tokens between rotations fail at most a
 * single grade before the next refresh runs.
 *
 * IMPORTANT (per Synkro Plan Review R003): the exported file lives ONLY on the
 * host under ~/.synkro/claude-creds/ and is bind-mounted at container runtime.
 * It must never be COPY'd into a Dockerfile or otherwise baked into image
 * layers — image layers are part of the publishable artefact and would leak
 * the customer's credentials to anyone who pulls the image.
 */

import { existsSync, mkdirSync, writeFileSync, chmodSync, readFileSync } from 'node:fs';
import { homedir, platform } from 'node:os';
import { join } from 'node:path';
import { spawnSync } from 'node:child_process';

export const SYNKRO_DIR = join(homedir(), '.synkro');
export const CLAUDE_CREDS_DIR = join(SYNKRO_DIR, 'claude-creds');
export const CLAUDE_CREDS_FILE = join(CLAUDE_CREDS_DIR, '.credentials.json');

// Cursor grading uses @cursor/sdk, which authenticates ONLY with a
// dashboard-issued Cursor API key — the macOS keychain OAuth token is
// rejected by the SDK. So there is no keychain bridge for Cursor: the user
// places their API key (cursor.com → Settings → API Keys) in this file and
// it is bind-mounted into the container.
export const CURSOR_CREDS_DIR = join(SYNKRO_DIR, 'cursor-creds');
export const CURSOR_API_KEY_FILE = join(CURSOR_CREDS_DIR, 'api-key');

// Keychain service name claude uses on macOS. Don't change without verifying
// against the installed Claude Code build first — name drift is silent and
// shows up later as "all my grades say unavailable."
const KEYCHAIN_SERVICE = 'Claude Code-credentials';

// launchd label for the periodic refresh agent.
const LAUNCHD_LABEL = 'com.synkro.cli.claude-creds-refresh';
const LAUNCHD_PLIST = join(homedir(), 'Library', 'LaunchAgents', `${LAUNCHD_LABEL}.plist`);
// The resident refresh loop lives in its own script file (not inlined in the
// plist) so we never have to XML-escape shell operators (<, >, &&, |, 2>&1).
const REFRESH_SCRIPT = join(SYNKRO_DIR, 'claude-creds-refresh-loop.sh');
const REFRESH_LOG = join(SYNKRO_DIR, 'claude-creds-refresh.log');

// Expiry-aware refresh cadence. The OLD design used a flat 45-min StartInterval
// on the assumption that Claude OAuth tokens live ~1h — they don't. Observed
// access-token lifetimes are as short as ~12 min, and the Keychain rotates the
// token underneath us at unpredictable times. A flat 45-min copy therefore left
// the container running an expired token for the majority of every cycle (all
// graders SICK, graded=0). The agent is now a resident loop that re-exports and
// sleeps until just before the *exported* token's expiry, so the container is
// never more than a couple minutes from current. We refresh CLAUDE creds here
// only (the host stays the sole owner of the Claude refresh-token chain — the
// container must NOT refresh it or it would rotate and invalidate the host
// Keychain's token, breaking host `claude` auth).
const REFRESH_EXPIRY_BUFFER_SECONDS = 120;  // wake this long before expiry
const MIN_REFRESH_INTERVAL_SECONDS = 30;    // floor: don't hammer the Keychain
const MAX_REFRESH_INTERVAL_SECONDS = 5 * 60; // ceiling: re-poll to catch rotations even on long-lived tokens

export class KeychainExportError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'KeychainExportError';
  }
}

/**
 * True when the platform actually needs the keychain bridge. Linux stores
 * creds in a file already; only darwin uses the keychain.
 */
export function needsKeychainBridge(): boolean {
  return platform() === 'darwin';
}

/**
 * Read the Claude Code credential blob from the macOS keychain. Returns null
 * if the entry doesn't exist (claude not signed in) or if the security CLI
 * fails for any reason. The caller should treat null as "auth not configured
 * yet" rather than a hard error — the user may not have launched claude yet.
 */
export function readKeychainCreds(): string | null {
  if (platform() !== 'darwin') return null;
  const r = spawnSync('security', ['find-generic-password', '-s', KEYCHAIN_SERVICE, '-w'], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  if (r.status !== 0) return null;
  const blob = (r.stdout || '').trim();
  return blob || null;
}

/**
 * One-shot export: pull creds from the keychain and write them to the
 * host-side file the container bind-mounts. Returns the absolute file path,
 * or null if there was nothing to export.
 *
 * The directory and file are chmodded to 700/600 so other users on the host
 * can't read the credentials. The container's bind mount preserves these
 * perms inside the namespace.
 */
export function exportKeychainCreds(): string | null {
  const blob = readKeychainCreds();
  if (!blob) return null;
  mkdirSync(CLAUDE_CREDS_DIR, { recursive: true });
  chmodSync(CLAUDE_CREDS_DIR, 0o700);
  // Write only when the blob actually changed. The container's cred-watchdog
  // keys its "host updated credentials file" → worker-restart on this file's
  // mtime, so rewriting an identical token every poll would churn the pool
  // needlessly. chmod is mtime-neutral, so re-asserting perms is harmless.
  let changed = true;
  try {
    if (existsSync(CLAUDE_CREDS_FILE)) {
      changed = readFileSync(CLAUDE_CREDS_FILE, 'utf-8') !== blob;
    }
  } catch { /* treat unreadable as changed */ }
  if (changed) writeFileSync(CLAUDE_CREDS_FILE, blob, 'utf-8');
  chmodSync(CLAUDE_CREDS_FILE, 0o600);
  return CLAUDE_CREDS_FILE;
}

/**
 * True when the user has placed a Cursor API key at CURSOR_API_KEY_FILE.
 * Used by the installer to decide whether the Cursor worker pool can run.
 * We never read the key value here — only confirm the file is present and
 * non-empty so the worker can pick it up via the bind mount.
 */
export function cursorApiKeyConfigured(): boolean {
  try {
    return existsSync(CURSOR_API_KEY_FILE)
      && readFileSync(CURSOR_API_KEY_FILE, 'utf-8').trim().length > 0;
  } catch {
    return false;
  }
}

/**
 * Persist a Cursor API key to CURSOR_API_KEY_FILE (0600, dir 0700). The
 * containerised worker bind-mounts the file and reads the key at grade time.
 * Same R003 rule as the Claude creds: host-only file, never baked into an
 * image layer.
 */
export function writeCursorApiKey(key: string): void {
  const trimmed = key.trim();
  if (!trimmed) return;
  mkdirSync(CURSOR_CREDS_DIR, { recursive: true });
  chmodSync(CURSOR_CREDS_DIR, 0o700);
  writeFileSync(CURSOR_API_KEY_FILE, trimmed, 'utf-8');
  chmodSync(CURSOR_API_KEY_FILE, 0o600);
}

/**
 * Check whether the stored Cursor API key is actually accepted by Cursor.
 * Hits the Cursor API's `GET /v1/me` (Basic auth — the key is the username).
 *
 * Returns true (accepted), false (definitively rejected — 401/403), or null
 * (could not determine: no key, network error, or an ambiguous status). A null
 * result must NOT be treated as invalid — a transient network blip should
 * never nag the user to re-enter a working key.
 */
export async function validateCursorApiKey(): Promise<boolean | null> {
  let key: string;
  try {
    key = readFileSync(CURSOR_API_KEY_FILE, 'utf-8').trim();
  } catch {
    return null;
  }
  if (!key) return null;
  try {
    const auth = Buffer.from(`${key}:`).toString('base64');
    const r = await fetch('https://api.cursor.com/v1/me', {
      headers: { Authorization: `Basic ${auth}` },
      signal: AbortSignal.timeout(8_000),
    });
    if (r.status === 401 || r.status === 403) return false;
    if (r.ok) return true;
    return null;
  } catch {
    return null;
  }
}

/**
 * Whether the exported Claude token is expired (or within the refresh buffer of
 * expiring). Expiry-based, not file-age based: a freshly *written* file can still
 * hold a near-dead token, and a long-lived token is fine no matter how old the
 * file is. Used by `synkro local-cc status` and the refresh loop.
 */
export function credsAreStale(): boolean {
  if (!existsSync(CLAUDE_CREDS_FILE)) return true;
  try {
    const raw = readFileSync(CLAUDE_CREDS_FILE, 'utf-8');
    const exp = JSON.parse(raw)?.claudeAiOauth?.expiresAt ?? 0; // epoch ms
    if (!exp) return true;
    return Date.now() >= exp - REFRESH_EXPIRY_BUFFER_SECONDS * 1000;
  } catch {
    return true;
  }
}

/** XML-escape a value destined for a plist <string> (paths are data, not code). */
function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Install the launchd agent that keeps the container's Claude creds fresh.
 *
 * This is a KeepAlive *resident loop* (not a flat StartInterval one-shot): it
 * re-exports the Keychain creds, then sleeps until just before the exported
 * token's expiry (clamped to [MIN, MAX]), so the bind-mounted copy is never
 * stale regardless of how short Claude's access tokens are or when the Keychain
 * rotates. It reuses the existing one-shot `synkro local-cc refresh-creds` for
 * the actual export, so it works with any installed CLI build. Idempotent —
 * overwrites the script + plist. Caller does the launchctl bootout/bootstrap.
 *
 * Security: the caller-supplied bin path and the creds path are passed to the
 * loop via launchd EnvironmentVariables (XML data, never shell-evaluated) and
 * read as "$SYNKRO_BIN" / "$SYNKRO_CF" — no caller string is interpolated into
 * the script body, so there is no shell-injection surface (CWE-78).
 *
 * Returns the absolute path to the written plist for the caller to load.
 */
export function writeRefreshAgent(synkroBinPath: string): string {
  if (platform() !== 'darwin') {
    throw new KeychainExportError('writeRefreshAgent is darwin-only');
  }
  mkdirSync(join(homedir(), 'Library', 'LaunchAgents'), { recursive: true });
  mkdirSync(SYNKRO_DIR, { recursive: true });

  // TS template literal: only `${...}` is interpolated. Here that is ONLY the
  // numeric tuning constants — never any path/string. `$VAR`, `$(...)`, and
  // `\${...}` survive verbatim into bash. expiresAt (epoch ms) is parsed with
  // sed (no jq dependency). We log only when the token actually changes.
  const script = `#!/bin/bash
# Generated by synkro (writeRefreshAgent). Expiry-aware Claude creds refresher.
# SYNKRO_BIN / SYNKRO_CF are injected by launchd as environment data — never
# interpolated into this script — so there is no shell-injection surface.
export PATH="/opt/homebrew/bin:/usr/local/bin:$PATH"
prev=""
while true; do
  { "$SYNKRO_BIN" local-cc refresh-creds || synkro local-cc refresh-creds; } >/dev/null 2>&1
  exp=$(sed -n 's/.*"expiresAt"[: ]*\\([0-9]\\{10,\\}\\).*/\\1/p' "$SYNKRO_CF" 2>/dev/null | head -1)
  now=$(date +%s)
  if [ -n "$exp" ]; then
    delay=$(( exp/1000 - now - ${REFRESH_EXPIRY_BUFFER_SECONDS} ))
  else
    delay=${MIN_REFRESH_INTERVAL_SECONDS}
  fi
  [ "$delay" -lt ${MIN_REFRESH_INTERVAL_SECONDS} ] && delay=${MIN_REFRESH_INTERVAL_SECONDS}
  [ "$delay" -gt ${MAX_REFRESH_INTERVAL_SECONDS} ] && delay=${MAX_REFRESH_INTERVAL_SECONDS}
  if [ "$exp" != "$prev" ]; then
    echo "[$(date '+%Y-%m-%dT%H:%M:%S')] claude creds refreshed (expiresAt=\${exp:-none}); next check in \${delay}s"
    prev="$exp"
  fi
  sleep "$delay"
done
`;
  writeFileSync(REFRESH_SCRIPT, script, 'utf-8');
  chmodSync(REFRESH_SCRIPT, 0o700);

  const plist = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
  <key>Label</key>
  <string>${LAUNCHD_LABEL}</string>
  <key>ProgramArguments</key>
  <array>
    <string>/bin/bash</string>
    <string>${xmlEscape(REFRESH_SCRIPT)}</string>
  </array>
  <key>EnvironmentVariables</key>
  <dict>
    <key>SYNKRO_BIN</key>
    <string>${xmlEscape(synkroBinPath)}</string>
    <key>SYNKRO_CF</key>
    <string>${xmlEscape(CLAUDE_CREDS_FILE)}</string>
  </dict>
  <key>KeepAlive</key>
  <true/>
  <key>RunAtLoad</key>
  <true/>
  <key>ThrottleInterval</key>
  <integer>${MIN_REFRESH_INTERVAL_SECONDS}</integer>
  <key>StandardErrorPath</key>
  <string>${xmlEscape(REFRESH_LOG)}</string>
  <key>StandardOutPath</key>
  <string>${xmlEscape(REFRESH_LOG)}</string>
</dict>
</plist>
`;
  writeFileSync(LAUNCHD_PLIST, plist, 'utf-8');
  return LAUNCHD_PLIST;
}

/**
 * Load (or reload) the launchd refresh agent. Safe to call multiple times.
 */
export function loadRefreshAgent(): void {
  if (platform() !== 'darwin') return;
  // bootout returns non-zero if it isn't loaded; that's fine, we just want to
  // ensure a clean state before bootstrap.
  spawnSync('launchctl', ['bootout', `gui/${process.getuid?.() ?? 501}`, LAUNCHD_PLIST], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  const r = spawnSync('launchctl', ['bootstrap', `gui/${process.getuid?.() ?? 501}`, LAUNCHD_PLIST], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  if (r.status !== 0) {
    throw new KeychainExportError(
      `launchctl bootstrap failed: ${r.stderr || r.stdout || 'unknown'}`,
    );
  }
}

/**
 * Stop the launchd refresh agent and remove the plist. Used by `synkro
 * disconnect` / `synkro uninstall` so we don't leave orphan agents behind.
 */
export function uninstallRefreshAgent(): void {
  if (platform() !== 'darwin') return;
  spawnSync('launchctl', ['bootout', `gui/${process.getuid?.() ?? 501}`, LAUNCHD_PLIST], {
    encoding: 'utf-8',
    timeout: 5_000,
  });
  try {
    const fs = require('node:fs');
    if (existsSync(LAUNCHD_PLIST)) fs.unlinkSync(LAUNCHD_PLIST);
    if (existsSync(REFRESH_SCRIPT)) fs.unlinkSync(REFRESH_SCRIPT);
  } catch { /* best-effort */ }
}

/**
 * Convenience wrapper for `synkro local-cc refresh-creds`. Re-exports the
 * Claude keychain creds. Cursor needs no refresh agent — its API key is a
 * static dashboard key the user supplies once; it doesn't rotate like the
 * Claude OAuth token.
 */
export function refreshCreds(): boolean {
  const path = exportKeychainCreds();
  return path !== null;
}

/**
 * Read the current exported creds (if any). Used by status commands.
 */
export function readExportedCreds(): string | null {
  try {
    return readFileSync(CLAUDE_CREDS_FILE, 'utf-8');
  } catch {
    return null;
  }
}
